import { useCallback, useEffect, useState } from "react";

interface Options {
  threshold?: number;
  rootMargin?: string;
  once?: boolean;
}

export function useInView<T extends HTMLElement = HTMLElement>(
  { threshold = 0.15, rootMargin = "0px 0px -10% 0px", once = true }: Options = {},
): { ref: (el: T | null) => void; inView: boolean } {
  // Callback ref + state so elements that mount later (e.g. after data
  // loads) still get observed — a plain RefObject would never re-run
  // the effect when the node attaches.
  const [node, setNode] = useState<T | null>(null);
  const [inView, setInView] = useState(false);
  const ref = useCallback((el: T | null) => setNode(el), []);

  useEffect(() => {
    if (!node) return;
    if (typeof IntersectionObserver === "undefined") {
      setInView(true);
      return;
    }
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (!entry) return;
        if (entry.isIntersecting) {
          setInView(true);
          if (once) obs.unobserve(entry.target);
        } else if (!once) {
          setInView(false);
        }
      },
      { threshold, rootMargin },
    );
    obs.observe(node);
    return () => obs.disconnect();
  }, [node, threshold, rootMargin, once]);

  return { ref, inView };
}
